import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowRight, ThermometerSnowflake, Wind, ShieldCheck, Hammer, TriangleAlert, CircleCheck, Wrench, ClipboardCheck } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const services = [{
  id: "encapsulation",
  title: "Crawl Space Encapsulation",
  subtitle: "Professional Sealing Standards",
  description: "The ultimate solution for Central Iowa foundation protection. Experts transform damp, dark crawl spaces into clean, dry, conditioned areas that improve indoor air quality and increase property value.",
  problem: "Iowa's high water table and humid summers turn open crawl spaces into mold factories, rotting floor joists and inviting pests into the home's living area.",
  solution: "Specialists install 20-mil antimicrobial liners that are mechanically fastened and permanently sealed, creating a durable barrier between the home and the raw earth.",
  benefits: ["Stops mold and mildew growth permanently", "Eliminates musty 'crawl space' odors for good", "Reduces heating and cooling bills by up to 20%", "Creates clean, dry, usable storage space"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1615668/img/crawl_space_encapsulation_liner.png",
  icon: <ShieldCheck className="size-6" />
}, {
  id: "vapor-barrier",
  title: "Vapor Barrier Systems",
  subtitle: "Expert Soil Protection",
  description: "Essential moisture protection designed specifically for the unique humidity challenges and heavy clay soil conditions of the Des Moines metro area.",
  problem: "Standard thin plastic liners used by many local builders tear easily, bunch up, and fail to stop 'earth-smells' and radon gases from infiltrating the home.",
  solution: "Experienced contractors install heavy-duty, multi-layer reinforced polyethylene barriers custom-tailored to every corner for a 100% monolithic seal.",
  benefits: ["High puncture and tear resistance for longevity", "Compatible with Iowa radon mitigation systems", "Class A fire-rated materials for safety", "Lifetime performance warranties for peace of mind"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1615668/img/vapor_barrier_installation_des_moines.png",
  icon: <Wind className="size-6" />
}, {
  id: "insulation",
  title: "Crawl Space Insulation",
  subtitle: "Thermal Comfort Solutions",
  description: "Upgrade from failing fiberglass to moisture-resistant rigid foam boards. These solutions keep Des Moines homes cozy and floors warm even during sub-zero Iowa winters.",
  problem: "Old fiberglass 'batts' absorb Iowa humidity, sag under weight, and become a breeding ground for rodents while leaving pipes exposed to freezing.",
  solution: "Specialists install closed-cell rigid foam insulation directly to foundation walls, creating a continuous thermal break that keeps heat in and cold air out.",
  benefits: ["Will not absorb moisture, rot, or sag over time", "Prevents frozen pipes during extreme Iowa freezes", "Significantly warmer first-level floors year-round", "Pest-resistant design that deters nesting animals"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1615668/img/crawl_space_rigid_foam_insulation.png",
  icon: <ThermometerSnowflake className="size-6" />
}, {
  id: "dehumidification",
  title: "Moisture Control Systems",
  subtitle: "Advanced Humidity Management",
  description: "Precision humidity control engineered for the Iowa climate. Industrial-grade systems keep crawl space air dry, protecting health and home structure.",
  problem: "Even with a barrier, Des Moines' summer humidity can reach 90%+. This moisture condenses on cold surfaces, fueling mold growth and warping hardwood flooring.",
  solution: "Experts integrate high-capacity, self-draining dehumidifiers that automatically maintain humidity levels below 55%, filtering the air.",
  benefits: ["Fully automatic, set-and-forget operation", "Built-in HEPA air filtration for cleaner air", "Protects expensive hardwood and laminate flooring", "Energy Star certified for low monthly operating costs"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1615668/img/dehumidifier_system_crawl_space.png",
  icon: <Hammer className="size-6" />
}, {
  id: "repair",
  title: "Crawl Space Repair",
  subtitle: "Professional Structural Stabilization",
  description: "Structural restoration for sagging or bouncy floors. Integrity is restored to the foundation using heavy-duty, engineered steel support jacks.",
  problem: "Excessive moisture weakens wood beams over time, leading to bouncy floors, sticking doors, and visible cracks in upstairs drywall.",
  solution: "Adjustable steel piers and beam reinforcement transfer the weight of the home to stable soil, leveling floors and stopping further settlement.",
  benefits: ["Permanently stabilizes sagging floor joists", "Corrects sticking doors and window frames", "Increases the structural capacity of the home", "Custom-engineered for specific foundation needs"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1615668/img/crawl_space_repair_des_moines.png",
  icon: <Wrench className="size-6" />
}, {
  id: "inspections",
  title: "Crawl Space Inspections",
  subtitle: "Professional Health Evaluations",
  description: "Expert 25-point crawl space evaluations for Des Moines homeowners. Professional assessments identify issues before they become major expenses.",
  problem: "Most homeowners never enter their crawl space, allowing leaks, mold, and structural rot to go unnoticed for years until they become catastrophic.",
  solution: "Detailed visual and digital inspections using moisture meters and thermal imaging provide a clear roadmap for long-term health and safety.",
  benefits: ["Detailed digital reports with photo documentation", "Early detection of mold and structural issues", "Peace of mind for home buyers and sellers", "Professional advice from local Iowa experts"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1615668/img/crawl_space_inspection_expert.png",
  icon: <ClipboardCheck className="size-6" />
}];
export default function OurSpecializedServices() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  return <section data-section-id="45865" id="our-specialized-solutions" className="bg-primary text-primary-foreground py-20 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={headerRef} className={cn("max-w-3xl mx-auto text-center mb-20 transition-all duration-700 transform", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-6">Trusted Crawl‑Space Professionals&nbsp;</h2>
          <p className="text-lg md:text-xl text-slate-300 leading-relaxed font-medium">Your Des Moines home deserves a foundation that's dry, healthy, and built to last. We connect homeowners with local experts who provide the engineering and commitment needed to eliminate moisture problems for good.</p>
        </div>

        <div className="space-y-24 lg:space-y-32">
          {services.map((service, index) => <ServiceBlock key={service.id} service={service} index={index} />)}
        </div>

        <div className="mt-24 text-center">
          <Link to="/ContactPage">
            <Button size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 text-lg px-8 py-6 h-auto shadow-xl rounded-sm transition-all font-bold tracking-tight">Request Free Inspection<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-arrow-right ml-2 size-5" aria-hidden="true" data-ast-id="f:/src/pages/Home/sections/OurSpecializedServices.tsx|sig:28i0wg" data-ast-start="7898" data-ast-end="7936"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg></Button>
          </Link>
        </div>
      </div>
    </section>;
}
function ServiceBlock({
  service,
  index
}: {
  service: typeof services[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const isEven = index % 2 === 0;
  return <div ref={ref} data-index={index} className={cn("flex flex-col lg:flex-row gap-12 lg:gap-20 items-center transition-all duration-1000", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12", !isEven && "lg:flex-row-reverse")}>
      <div className="w-full lg:w-1/2 relative group">
        <div className="absolute inset-0 bg-secondary/10 rounded-sm transform rotate-1 transition-transform group-hover:rotate-0 duration-500" />
        <div className="relative rounded-sm overflow-hidden shadow-2xl border border-white/10 bg-slate-900 aspect-video">
          <img src={service.image} alt={service.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute bottom-6 left-6 right-6 text-white">
            <div className="inline-flex items-center gap-2 bg-secondary/90 backdrop-blur-sm px-4 py-1.5 rounded-sm text-xs font-bold uppercase tracking-widest mb-2 shadow-lg">
              {service.icon}
              {service.subtitle}
            </div>
          </div>
        </div>
      </div>

      <div className="w-full lg:w-1/2 space-y-8">
        <div>
          <h3 className="text-3xl font-bold text-white mb-4 tracking-tight">{service.title}</h3>
          <p className="text-slate-300 text-lg leading-relaxed">
            {service.description}
          </p>
        </div>

        <div className="grid gap-6">
          <div className="relative pl-6 border-l-4 border-destructive bg-destructive/10 p-5 rounded-sm">
            <div className="flex items-center gap-2 text-red-400 font-bold mb-2 tracking-tight">
              <TriangleAlert className="size-5" />
              <span>The Problem</span>
            </div>
            <p className="text-slate-200 text-sm md:text-base">
              {service.problem}
            </p>
          </div>

          <div className="relative pl-6 border-l-4 border-chart-2 bg-chart-2/10 p-5 rounded-sm">
            <div className="flex items-center gap-2 text-teal-400 font-bold mb-2 tracking-tight">
              <CircleCheck className="size-5" />
              <span>The Solution</span>
            </div>
            <p className="text-slate-200 text-sm md:text-base">
              {service.solution}
            </p>
          </div>
        </div>

        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="benefits" className="border-white/10">
            <AccordionTrigger className="text-white hover:text-secondary hover:no-underline py-4">
              <span className="text-base font-bold tracking-tight">Technical Specifications & Benefits</span>
            </AccordionTrigger>
            <AccordionContent>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 pb-4">
                {service.benefits.map((benefit, i) => <li key={i} className="flex items-start gap-2 text-slate-300 text-sm">
                    <div className="mt-1 size-1.5 rounded-lg bg-secondary shrink-0" />
                    {benefit}
                  </li>)}
              </ul>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </div>;
}